import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { getCameraSets, createCameraSet, updateCameraSet, deleteCameraSet, getCamerasForSets } from 'api/canvms';
import { displayErrorMessage, displaySuccessMessage } from 'redux/notificationBar/notificationBarSlice';
import type { CameraSet, CameraSetCreateInput, CameraSetUpdateInput, CameraForSelection } from '../types/canvision.types';

export function useCameraSets() {
  const dispatch = useDispatch();
  const [sets, setSets] = useState<CameraSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  const fetchSets = useCallback(async (isRefresh = false) => {
    try {
      if (!isRefresh && !initialLoadDone.current) {
        setLoading(true);
      }
      setError(null);
      const response = await getCameraSets();
      setSets(response.data?.sets || []);
      initialLoadDone.current = true;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch camera sets';
      console.error('[CANVMS/fetchCameraSets] Error fetching camera sets:', err);
      setError(message);
      dispatch(displayErrorMessage(message));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    initialLoadDone.current = false;
    fetchSets(false);
  }, [fetchSets]);

  const refetch = useCallback(() => fetchSets(true), [fetchSets]);

  const create = useCallback(async (data: CameraSetCreateInput): Promise<CameraSet> => {
    try {
      const response = await createCameraSet(data);
      await fetchSets(true);
      dispatch(displaySuccessMessage('Camera set created successfully'));
      return response.data;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to create camera set';
      console.error('[CANVMS/createCameraSet] Error creating camera set:', err);
      dispatch(displayErrorMessage(message));
      throw err;
    }
  }, [fetchSets, dispatch]);

  const update = useCallback(async (setId: number, data: CameraSetUpdateInput): Promise<CameraSet> => {
    try {
      const response = await updateCameraSet(setId, data);
      await fetchSets(true);
      dispatch(displaySuccessMessage('Camera set updated successfully'));
      return response.data;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to update camera set';
      console.error('[CANVMS/updateCameraSet] Error updating camera set:', err);
      dispatch(displayErrorMessage(message));
      throw err;
    }
  }, [fetchSets, dispatch]);

  const remove = useCallback(async (setId: number): Promise<void> => {
    try {
      await deleteCameraSet(setId);
      await fetchSets(true);
      dispatch(displaySuccessMessage('Camera set deleted successfully'));
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to delete camera set';
      console.error('[CANVMS/deleteCameraSet] Error deleting camera set:', err);
      dispatch(displayErrorMessage(message));
      throw err;
    }
  }, [fetchSets, dispatch]);

  return { sets, loading, error, refetch, create, update, remove };
}

export function useAvailableCameras() {
  const dispatch = useDispatch();
  const [cameras, setCameras] = useState<CameraForSelection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCameras = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCamerasForSets();
      setCameras(response.data?.cameras || []);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch cameras';
      console.error('[CANVMS/fetchAvailableCameras] Error fetching available cameras:', err);
      setError(message);
      dispatch(displayErrorMessage(message));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchCameras();
  }, [fetchCameras]);

  const refetch = useCallback(() => fetchCameras(), [fetchCameras]);

  return { cameras, loading, error, refetch };
}
